import { FormikProps } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  apiInstance,
  Dispatch,
  GET_DATA_USERS_PROVIDERS_CORE,
  RootState,
} from "shared";
import * as Yup from "yup";
import { columnsUsersType } from "../types/columsUsers.types";
import axios from "axios";
import Swal from "sweetalert2";
import { optionsDropdowns } from "shared/store/states";

export const useFormUpdateUsers = (
  username: string,
  user?: columnsUsersType,
) => {
  const dispatch = useDispatch<Dispatch>();
  const {
    departments,
    cities: localities,
    typeHealthProvider,
  } = useSelector((state: RootState) => state.optionsDropdowns);
  const { userToken } = useSelector(
    (state: RootState) => state.electronicDocuments,
  );
  const [cities, setCities] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const formikRef = useRef<FormikProps<any>>(null);
  const initialValues = {
    organizationCode: user?.organizationCode || "",
    employerIdNumber: user?.employerIdNumber || "",
    type: user?.natureIdentification.id || "",
    givenName: user?.givenName || "",
    acreedor: user?.acreedor || "",
    region: user?.region.id || "",
    locality: user?.locality.id || "",
    streetAddress: user?.streetAddress || "",
    email: user?.email || "",
    email2: user?.emailTwo || "",
    phoneNumberOne: user?.phoneNumberOne ? user?.phoneNumberOne : "",
    phoneNumberTwo: user?.phoneNumberTwo ? user?.phoneNumberTwo : "",
    contactPersonOne: user?.contactPersonOne || "",
    contactPersonTwo: user?.contactPersonTwo || "",
    contactPersonOccupationOne: user?.contactPersonOccupationOne || "",
    contactPersonOccupationTwo: user?.contactPersonOccupationTwo || "",
  };

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      organizationCode: Yup.string()
        .trim()
        .required("campo requerido")
        .min(12, "Debe tener al menos 12 caracteres")
        .matches(/^[0-9]+$/, "Deben ser solo números"),
      employerIdNumber: Yup.string().trim().required("campo requerido"),
      type: Yup.string().trim().required("campo requerido").max(50),
      givenName: Yup.string().max(50).trim().required("campo requerido"),
      acreedor: Yup.string()
        .trim()
        .required("campo requerido")
        .max(20)
        .matches(/^[0-9]+$/, "Deben ser solo números"),
      streetAddress: Yup.string().trim().required("campo requerido").max(150),
      email: Yup.string()
        .trim()
        .email("Correo inválido")
        .max(50)
        .required("campo requerido"),
      email2: Yup.string().trim().email("Correo inválido").max(50),
      phoneNumberOne: Yup.string()
        .trim()
        .required("Campo requerido")
        .max(10, "Debe tener 10 dígitos")
        .min(10, "Debe tener 10 dígitos"),
      phoneNumberTwo: Yup.string()
        .trim()
        .max(10, "Debe tener 10 dígitos")
        .min(10, "Debe tener 10 dígitos"),
      contactPersonOne: Yup.string().trim().required("campo requerido").max(50),
      contactPersonTwo: Yup.string().trim().max(50),
      contactPersonOccupationOne: Yup.string()
        .trim()
        .required("campo requerido")
        .max(50),
      contactPersonOccupationTwo: Yup.string().trim().max(50),
      region: Yup.string().trim().required("campo requerido"),
      locality: Yup.string().trim().required("campo requerido"),
    });
  }, []);

  const handleBlurSelectDepartment = (event: any) => {
    const { value } = event.target;
    formikRef.current?.setValues((values: any) => ({
      ...values,
      city: "",
    }));
    dispatch.optionsDropdowns.addCities([]);

    if (!value) return;

    dispatch.optionsDropdowns?.getCities(value);
    setCities(!cities);
  };

  const handleSubmit = async (values: any) => {
    const region = departments.find(
      (dep: any) => dep.id === Number(values.region),
    )?.name;
    const city = localities.find(
      (city: any) => city.id === Number(values.locality),
    )?.name;
    const type = typeHealthProvider.find(
      (type: any) => type.id === Number(values.type),
    )?.name;
    setIsLoading(true);
    if (user) {
      try {
        await axios.put(
          `${process.env.REACT_APP_OAUTH}/users/${user.id}`,
          {
            organizationCode: values.organizationCode,
            employerIdNumber: values.employerIdNumber,
            type: type,
            typeId: values.type,
            givenName: values.givenName,
            companyMatchCode: values.acreedor,
            streetAddress: values.streetAddress,
            email: values.email,
            email2: values.email2,
            phoneNumber: values.phoneNumberOne,
            phoneNumberTwo: values.phoneNumberTwo,
            contactPersonOne: values.contactPersonOne,
            contactPersonTwo: values.contactPersonTwo,
            contactPersonOccupationOne: values.contactPersonOccupationOne,
            contactPersonOccupationTwo: values.contactPersonOccupationTwo,
            region: region,
            regionId: values.region,
            locality: city,
            localityId: values.locality,
            username,
          },
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
              "Content-Type": "application/json",
            },
          },
        );
        await apiInstance.put(
          `${GET_DATA_USERS_PROVIDERS_CORE}/${user.organizationCode}`,
          {
            code: values.organizationCode,
            employerIdNumber: values.employerIdNumber,
            typeId: Number(values.type),
            name: values.givenName,
            companyMatchCode: values.acreedor,
            address: values.streetAddress,
            email: values.email,
            email2: values.email2,
            phoneNumberOne: values.phoneNumberOne,
            phoneNumberTwo: values.phoneNumberTwo,
            contactPersonOne: values.contactPersonOne,
            contactPersonTwo: values.contactPersonTwo,
            contactPersonOccupationOne: values.contactPersonOccupationOne,
            contactPersonOccupationTwo: values.contactPersonOccupationTwo,
            provinceId: values.region,
            cityId: Number(values.locality),
            initials: "",
          },
        );
        setIsLoading(false);
        Swal.fire({
          icon: "success",
          title: "Usuario actualizado",
          text: "La información se ha almacenado correctamente",
          confirmButtonText: "Aceptar",
        }).then(() => {
          formikRef.current?.resetForm();
          dispatch.messageModal.cleanStateMessage();
          dispatch.electronicDocuments.getAllUsersProviders(
            { page: 1, size: 50 },
            { url: `${process.env.REACT_APP_OAUTH}/users`, userToken },
          );
        });
      } catch (error: any) {
        setIsLoading(false);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text:
            error.response?.data?.message || "Error al actualizar el usuario",
        });
      }
    } else {
      try {
        await axios.post(
          `${process.env.REACT_APP_OAUTH}/users`,
          {
            organizationCode: values.organizationCode,
            employerIdNumber: values.employerIdNumber,
            type: type,
            typeId: values.type,
            givenName: values.givenName,
            companyMatchCode: values.acreedor,
            streetAddress: values.streetAddress,
            email: values.email,
            email2: values.email2,
            phoneNumber: values.phoneNumberOne,
            phoneNumberTwo: values.phoneNumberTwo,
            contactPersonOne: values.contactPersonOne,
            contactPersonTwo: values.contactPersonTwo,
            contactPersonOccupationOne: values.contactPersonOccupationOne,
            contactPersonOccupationTwo: values.contactPersonOccupationTwo,
            region: region,
            regionId: values.region,
            locality: city,
            localityId: values.locality,
            username: values.organizationCode,
          },
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
              "Content-Type": "application/json",
            },
          },
        );
        await apiInstance.post(GET_DATA_USERS_PROVIDERS_CORE, {
          code: values.organizationCode,
          employerIdNumber: values.employerIdNumber,
          typeId: values.type,
          name: values.givenName,
          creditor: values.acreedor,
          address: values.streetAddress,
          email: values.email,
          email2: values.email2,
          phoneNumberOne: values.phoneNumberOne,
          phoneNumberTwo: values.phoneNumberTwo,
          contactPersonOne: values.contactPersonOne,
          contactPersonTwo: values.contactPersonTwo,
          contactPersonOccupationOne: values.contactPersonOccupationOne,
          contactPersonOccupationTwo: values.contactPersonOccupationTwo,
          provinceId: values.region,

          cityId: values.locality,
          initials: "",
        });
        setIsLoading(false);
        Swal.fire({
          icon: "success",
          title: "Usuario creado",
          text: "La información se ha almacenado correctamente",
          confirmButtonText: "Aceptar",
        }).then(() => {
          formikRef.current?.resetForm();
          dispatch.messageModal.cleanStateMessage();
          dispatch.electronicDocuments.getAllUsersProviders(
            { page: 1, size: 50 },
            { url: `${process.env.REACT_APP_OAUTH}/users`, userToken },
          );
        });
      } catch (error: any) {
        setIsLoading(false);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: error.response?.data?.message || "Error al crear el usuario",
        });
      }
    }
  };

  const onlyNumbers = (event: any) => {
    const text = (event.clipboardData || window.Clipboard).getData("text");

    if (!/^(\d+)$/.test(text)) {
      event.preventDefault();
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (departments.length === 0) {
      dispatch.optionsDropdowns.getDepartments();
    }
  }, [departments?.length == 0]);

  useEffect(() => {
    if (typeHealthProvider.length === 0) {
      dispatch.optionsDropdowns.getTypeHealthProvider();
    }
  }, [typeHealthProvider?.length == 0]);

  useEffect(() => {
    user?.region && dispatch.optionsDropdowns.getCities(user.region.id);
  }, [user]);

  return {
    initialValues,
    formikRef,
    isLoading,
    validationSchema,
    handleBlurSelectDepartment,
    handleSubmit,
    onlyNumbers,
  };
};
